import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';

const AppNavbar = () => {
  // helpers
  const toId = (s) => s.trim().toLowerCase().replace(/\s+/g, "-");
  const isAbout = (s) => s.trim().toLowerCase().startsWith("who we are");
  const isHome = (s) => s.trim().toLowerCase() === "home";
  const isProducts = (s) => s.trim().toLowerCase() === "products";


  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(toId("home"));
  const [productsOpen, setProductsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const sections = ["home", "who we are", "services", "products"];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
      if (window.scrollY < 20) {
        setActiveSection(toId("home"));
        return;
      }

      const scrollPosition = window.scrollY + 100;
      for (const section of sections) {
        const id = toId(section);
        const el = document.getElementById(id);
        if (el) {
          const top = el.offsetTop;
          const height = el.offsetHeight;
          if (scrollPosition >= top && scrollPosition < top + height) {
            setActiveSection(id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [sections]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (productsOpen && dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setProductsOpen(false);
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [productsOpen]);

  useEffect(() => {
    // close products dropdown on route change
    setProductsOpen(false);
  }, [location.pathname]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const yOffset = -72;
      const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
      setActiveSection(id);
      setMobileMenuOpen(false);
    }
  };

  return (
    <>
      <style>{`
        .navbar-transparent {
          background-color: transparent;
          transition: background-color 0.3s ease, box-shadow 0.3s ease;
        }

        .navbar-scrolled {
          background-color: #ffffff !important;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          transition: background-color 0.3s ease, box-shadow 0.3s ease;
        }

        /* nav link colors */
        .navbar-transparent .nav-link,
        .navbar-scrolled .nav-link {
          color: #1A2233 !important;
          transition: color 0.2s;
        }

        .navbar .nav-link.active {
          color: #1976d2 !important; /* Primary color */
          background: rgba(25, 118, 210, 0.08);
          border-radius: 999px;
          font-weight: 600;
          position: relative;
          transition: background 0.2s, color 0.2s;
        }
        .navbar .nav-link.active::after {
          content: "";
          display: block;
          margin: 0 auto;
          width: 60%;
          height: 3px;
          border-radius: 2px;
          background: #1976d2;
          margin-top: 4px;
          transition: width 0.2s;
        }
        .navbar .nav-link {
          padding: 8px 18px !important;
          margin: 0 2px;
        }

        .navbar .nav-link:hover,
        .navbar .nav-link:focus {
          background: rgba(25, 118, 210, 0.06);
          color: #1976d2 !important;
          border-radius: 999px;
          transition: background 0.2s, color 0.2s;
        }

        /* Dropdown styles */
        .navbar .dropdown-menu {
          background: #ffffff;
          border: 1px solid #e3e8ee;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          animation: fadeIn 0.2s ease;
          min-width: 280px;
        }

        .navbar .dropdown-item {
          color: #1A2233 !important;
          padding: 10px 16px;
          transition: all 0.2s ease;
          font-weight: 500;
          font-size: 0.9rem;
        }

        .navbar .dropdown-item:hover {
          background: rgba(25, 118, 210, 0.08);
          color: #1976d2 !important;
        }

        .navbar .dropdown-divider {
          margin: 8px 0;
          border-color: #e3e8ee;
        }

        .navbar .dropdown-item.sub-item {
          padding-left: 32px;
          font-size: 0.8rem;
        }

        /* make sure nav-link can show the pseudo-element */
        .navbar .nav-link {
          position: relative;
          z-index: 1;
        }

        /* stronger selector and !important so Bootstrap won't hide/override it */
        .navbar .nav-link.dropdown-toggle::after {
          content: "" !important;
          display: inline-block !important;
          width: 0 !important;
          height: 0 !important;
          margin-left: 0.45rem !important;
          vertical-align: 0.125em !important;
          border-left: 0.35em solid transparent !important;
          border-right: 0.35em solid transparent !important;
          border-top: 0.35em solid currentColor !important; /* force triangle color */
          transition: transform 0.18s ease, opacity 0.18s ease !important;
          opacity: 1 !important;
        }

        /* rotate when open */
        .navbar .nav-link.dropdown-toggle[aria-expanded="true"]::after {
          transform: rotate(180deg) !important;
        }

        /* fallback caret (visible on small screens if needed) */
        .navbar .nav-link .caret-fallback {
          display: none;
          margin-left: 0.35rem;
        }
        @media (max-width: 768px) {
          .navbar .nav-link .caret-fallback { display: inline-block; }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (max-width: 578px) {
           .navbar-transparent {
           background-color: #ffffff ;
        }
        .navbar .nav-link {
          margin: 2px 0;
        }
      }
    `}</style>

      <nav
        className={`navbar navbar-expand-lg fixed-top ${scrolled ? "navbar-scrolled" : "navbar-transparent"
          }`}
      >
        <div className="container">
          <Link className="navbar-brand" to="/">
            <img
              src="../logo.svg"
              alt="Logo"
              className="d-inline-block align-text-top me-2"
              style={{ maxWidth: "70px" }}
            />
          </Link>

          <button
            className="navbar-toggler"
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle navigation"
            style={{ border: "none" }}
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          <div className={`collapse navbar-collapse ${mobileMenuOpen ? "show" : ""}`}>
            <ul className="navbar-nav ms-auto mb-2 mb-lg-0">
              {sections.map((section) => {
                const id = toId(section);
                if (isAbout(section)) {
                  return (
                    <li className="nav-item" key={section}>
                      <Link
                        className={`nav-link ${activeSection === id ? "active" : ""}`}
                        to="/about"
                        state={{ scrollTo: id }}
                        onClick={() => { setMobileMenuOpen(false); setActiveSection(id); }}
                      >
                        {section.charAt(0).toUpperCase() + section.slice(1)}
                      </Link>
                    </li>
                  );
                }
                if (isHome(section)) {
                  // Use Link for Home
                  return (
                    <li className="nav-item" key={section}>
                      {location.pathname === "/" ? (
                        <a
                          className={`nav-link ${activeSection === "home" ? "active" : ""}`}
                          href="#home"
                          onClick={e => {
                            e.preventDefault();
                            window.scrollTo({ top: 0, behavior: "smooth" });
                            setActiveSection(toId("home"));
                            setMobileMenuOpen(false);
                          }}
                        >
                          {section.charAt(0).toUpperCase() + section.slice(1)}
                        </a>
                      ) : (
                        <Link
                          className={`nav-link ${location.pathname === "/" && activeSection === "home" ? "active" : ""}`}
                          to="/"
                          onClick={() => {
                            setMobileMenuOpen(false);
                            setActiveSection(toId("home"));
                          }}
                        >
                          {section.charAt(0).toUpperCase() + section.slice(1)}
                        </Link>)}
                    </li>
                  );
                }

                if (isProducts(section)) {
                  return (
                    <li
                      className={`nav-item dropdown ${productsOpen ? "show" : ""}`}
                      key={section}
                      ref={dropdownRef}
                      onMouseEnter={() => {
                        if (window.innerWidth >= 992) setProductsOpen(true);
                      }}
                      onMouseLeave={() => {
                        if (window.innerWidth >= 992) setProductsOpen(false);
                      }}
                    >
                      <a
                        className={`nav-link dropdown-toggle ${activeSection === id ? "active" : ""}`}
                        href={`#${id}`}
                        id="productsDropdown"
                        role="button"
                        aria-expanded={productsOpen}
                        onClick={(e) => {
                          e.preventDefault();
                          if (window.innerWidth < 992) {
                            setProductsOpen(v => !v);
                            setActiveSection(id);
                            return;
                          }
                          setProductsOpen(false);
                          setActiveSection(id);
                          setMobileMenuOpen(false);
                          if (location.pathname === "/") {
                            scrollToSection(id);
                          } else {
                            navigate("/", { state: { scrollTo: id } });
                          }
                        }}
                      >
                        {section.charAt(0).toUpperCase() + section.slice(1)}
                      </a>

                      <ul className={`dropdown-menu ${productsOpen ? "show" : ""}`} aria-labelledby="productsDropdown">
                        <li>
                          <a
                            className="dropdown-item"
                            href="#products"
                            onClick={(e) => {
                              e.preventDefault();
                              setProductsOpen(false);
                              setActiveSection(id);
                              // close mobile menu after selecting an item
                              setMobileMenuOpen(false);
                              if (location.pathname === "/") {
                                scrollToSection(id);
                              } else {
                                navigate("/", { state: { scrollTo: id } });
                              }
                            }}
                          >
                            All Products
                          </a>
                        </li>

                        <li><hr className="dropdown-divider" /></li>

                        <li>
                          <a
                            className="dropdown-item"
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              setProductsOpen(false);
                              setActiveSection(id);
                              navigate("/products/contactless-water-dispenser");
                              setMobileMenuOpen(false);
                            }}
                          >
                            Contactless Water Dispensing System
                          </a>
                        </li>

                        <li>
                          <a
                            className="dropdown-item"
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              setProductsOpen(false);
                              setActiveSection(id);
                              navigate("/products/smart-facility-monitoring-system");
                              setMobileMenuOpen(false);
                            }}
                          >
                            Smart Facility Monitoring System
                          </a>
                        </li>

                        <li>
                          <a
                            className="dropdown-item sub-item"
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              setProductsOpen(false);
                              setActiveSection(id);
                              navigate("/products/smart-dustbin-monitoring-system");
                              setMobileMenuOpen(false);
                            }}
                          >
                            • Smart Dustbin Monitoring System
                          </a>
                        </li>
                        <li>
                          <a
                            className="dropdown-item sub-item"
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              setProductsOpen(false);
                              setActiveSection(id);
                              navigate("/products/indoor-air-quality-monitoring-system");
                              setMobileMenuOpen(false);
                            }}
                          >
                            • Indoor Air Quality Monitoring System
                          </a>
                        </li>
                        <li>
                          <a
                            className="dropdown-item sub-item"
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              setProductsOpen(false);
                              setActiveSection(id);
                              navigate("/products/occupancy-counting-system");
                              setMobileMenuOpen(false);
                            }}
                          >
                            • Occupancy Counting System
                          </a>
                        </li>
                        <li>
                          <a
                            className="dropdown-item"
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              setProductsOpen(false);
                              setActiveSection(id);
                              navigate("/products/smart-energy-monitoring-system");
                              setMobileMenuOpen(false);
                            }}
                          >
                            Smart Energy Monitoring System
                          </a>
                        </li>
                      </ul>
                    </li>
                  );
                }
// ...existing code...

                return (
                  <li className="nav-item" key={section}>
                    {location.pathname === "/" ? (
                      <a
                        className={`nav-link ${activeSection === section ? "active" : ""}`}
                        href={`#${id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          scrollToSection(id);
                        }}
                      >
                        {section.charAt(0).toUpperCase() + section.slice(1)}
                      </a>
                    ) : (
                      <Link
                        className={`nav-link ${activeSection === id ? "active" : ""}`}
                        to="/"
                        state={{ scrollTo: id }}
                        onClick={() => { setMobileMenuOpen(false); setActiveSection(id); }}
                      >
                        {section.charAt(0).toUpperCase() + section.slice(1)}
                      </Link>
                    )}
                  </li>
                );
              }
              )}
            </ul>

            <div className="d-flex ms-lg-3">
              <button
                className="btn btn-primary rounded-pill px-4"
                onClick={() => {
                  if (location.pathname === "/") {
                    scrollToSection(toId("contact"));
                  } else {
                    navigate("/", { state: { scrollTo: toId("contact") } });
                    setMobileMenuOpen(false);
                  }
                }}
              >
                Contact us
              </button>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default AppNavbar;
